import moment from 'moment-timezone';
import { cloneDeep } from 'lodash';
import { customerOverrideForDrilldown } from '@src/components/app-viewer-sessions/utils';
import instance from './axiosInstance';
import { appendFlowIdentifier, getAllFlowInstancesFilter } from '@src/utils/filter';
import { ALL_FLOWS_IDENTIFIER } from '@src/components/app-session-timeline/constants';

const DEFAULT_TIMELINE_RULES: {
  field: string;
  op: string;
  value: string | number;
  display?: string;
}[][] = [
  [
    {
      field: 'event_name',
      op: 'not_eq',
      value: 'conviva_page_ping',
      display: 'conviva_page_ping',
    },
  ],
  [
    {
      field: 'event_name',
      op: 'not_eq',
      value: 'conviva_periodic_heartbeat',
      display: 'conviva_periodic_heartbeat',
    },
  ],
];

export function getFilter4UserTimeline(
  viewerId: string[],
  clientId: string | null | undefined,
  flowIdentifier?: string,
  defaultRules = DEFAULT_TIMELINE_RULES,
) {
  const res = cloneDeep(defaultRules) ?? [];

  if (viewerId.length > 0) {
    const viewerIdFilters = viewerId.map((item) => ({
      field: 'app_user_id',
      op: 'eq',
      value: item,
    }));
    res.push(viewerIdFilters);
  }

  if (clientId) {
    res.push([
      {
        field: 'client_id',
        op: 'eq',
        value: clientId,
      },
    ]);
  }

  if (flowIdentifier === ALL_FLOWS_IDENTIFIER) {
    return getAllFlowInstancesFilter(res);
  } else if (flowIdentifier) {
    return appendFlowIdentifier(res, flowIdentifier);
  }

  return res;
}

export interface ISessionTimelineSummaryDataItem {
  name: string;
  value: string | number;
}

export interface ISessionTimelineSummaryData {
  userInfoData: ISessionTimelineSummaryDataItem[];
  eventSummaryData: ISessionTimelineSummaryDataItem[];
}

export interface ISessionTimelineTableDataItem {
  sessionId: string;
  eventTimeStampStart: string;
  eventTimeStampEnd: string;
  userActiveTime: number;
  appCrashes: number;
  noOfFailedReq: number;
  totalEvents: number;
  index: number | string;
  inSession: number | string;
}

export interface UserTimelineEvent {
  derivedTstamp: string;
  sessionId: string;
  inSession: 0 | 1;
  eventName: string;
  /** not exist for App Scaled */
  sessionDuration: string;
  eventAttributes: object | {};
  // [props: string]: number | string | object;
}

export interface CombinationFilterItem {
  type: string;
  rel: string;
  combinations: Array<ExpressionFilterItem | CombinationFilterItem>;
}

export interface ExpressionFilterItem {
  op: string;
  val: string;
  type: string;
  field: string;
}

export interface MetricFilter {
  dimension: string;
  lower?: string;
  upper?: string;
  type: 'bound';
}

export interface MarioQuery {
  project: string;
  query_id: string | number;
  with_total?: boolean;
  model: string;
  explore: string;
  fields: string[];
  filters?: CombinationFilterItem | ExpressionFilterItem;
  sorts?: string[];
  limit?: number;
  query_timezone: string;
  parameters?: Array<Record<string, any>>;
  metricFilter?: MetricFilter;
  drill_down_filters?: CombinationFilterItem | ExpressionFilterItem;
  options?: object;
}

export const getTimelineSummary = (
  reqBody,
  options: {
    viewerId: string[];
    ts: string;
    clientId?: string | null;
    flowIdentifier?: string;
  },
  http = instance,
) => {
  const queries = cloneDeep(reqBody.queries);
  const { viewerId, ts, clientId, flowIdentifier } = options;
  const now = new Date();
  const last = new Date(now.getTime() - 24 * 3600000);
  const interval = ts || `${moment(last)}/${moment(now)}`;

  return http.post<ISessionTimelineSummaryData>('/appData/timeline/summary', {
    queries: queries.map((q) => {
      return {
        ...q,
        options: { withTotals: false },
        metrics: [],
        type: '',
        interval,
        granularity: 'ALL',
        filter: getFilter4UserTimeline(viewerId, clientId, flowIdentifier),
      };
    }),
  });
};

export const getTimelineSessions = (
  reqBody,
  options: {
    viewerId: string[];
    ts: string;
    clientId?: string | null;
    fields: string[];
  },
  http = instance,
) => {
  const queries = cloneDeep(reqBody.queries);
  const { viewerId, ts, clientId, fields } = options;
  const now = new Date();
  const last = new Date(now.getTime() - 24 * 3600000);
  const interval = ts || `${moment(last)}/${moment(now)}`;

  return http.post<ISessionTimelineTableDataItem[]>('/appData/timeline/sessions', {
    queries: queries.map((q) => {
      return {
        ...q,
        options: { withTotals: false },
        metrics: [],
        type: '',
        interval,
        granularity: 'ALL',
        filter: getFilter4UserTimeline(viewerId, clientId),
        extraFields: fields,
      };
    }),
  });
};

// TODO: Deprecate
export const getTimelineEventList = (
  reqBody,
  options: {
    viewerId: string[];
    ts: string;
    clientId?: string | null;
    sessionId: string;
    enableNW: boolean;
    globalFilters: Record<string, string>[][];
    enableUserId: boolean;
    customer_id: string;
    isFlowTimeline?: boolean;
  },
  http = instance,
) => {
  const queries = cloneDeep(reqBody.queries);
  const {
    viewerId,
    ts,
    clientId,
    sessionId,
    enableNW,
    globalFilters,
    enableUserId,
    customer_id,
    isFlowTimeline = false,
  } = options;
  const now = new Date();
  const last = new Date(now.getTime() - 24 * 3600000);
  const interval = ts || `${moment(last)}/${moment(now)}`;
  const filter = getFilter4UserTimeline(isFlowTimeline ? [] : viewerId, clientId);

  if (!isFlowTimeline) {
    filter.push([
      {
        field: 'session_id',
        value: sessionId,
        op: 'eq',
      },
    ]);
  }

  if (!enableNW) {
    filter.push([
      {
        field: 'event_name',
        value: 'conviva_network_request',
        op: 'not_eq',
      },
    ]);
  }

  const set = new Set<string>();
  if (globalFilters && globalFilters.length > 0) {
    globalFilters.forEach((andFilter) => {
      andFilter.forEach((orFilter) => {
        set.add(orFilter.field);
      });
    });
  }
  if (enableUserId) {
    const userIdKey = customerOverrideForDrilldown[customer_id] ?? 'app_user_id';
    set.add(userIdKey);
  }
  const extraFields = Array.from(set);

  return http.post<{
    eventList: UserTimelineEvent[];
    requestBody: MarioQuery;
  }>('/appData/timeline/eventList', {
    queries: queries.map((q) => {
      return {
        ...q,
        options: { withTotals: false },
        metrics: [],
        type: '',
        interval,
        granularity: 'ALL',
        filter,
        extraFields,
      };
    }),
  });
};

export const getTimelineEventListNew = (
  reqBody,
  options: {
    viewerId: string[];
    ts: string;
    clientId?: string | null;
    sessionId: string | string[];
    enableNW: boolean;
    globalFilters: Record<string, string>[][];
    enableUserId: boolean;
    customer_id: string;
  },
  http = instance,
) => {
  const queries = cloneDeep(reqBody.queries);
  const { viewerId, ts, clientId, sessionId, enableNW, globalFilters, enableUserId, customer_id } =
    options;
  const now = new Date();
  const last = new Date(now.getTime() - 24 * 3600000);
  const interval = ts || `${moment(last)}/${moment(now)}`;
  const filter = getFilter4UserTimeline(viewerId, clientId);

  if (Array.isArray(sessionId)) {
    filter.push(
      sessionId.map((id) => ({
        field: 'session_id',
        value: id,
        op: 'eq',
      })),
    );
  } else if (sessionId) {
    filter.push([
      {
        field: 'session_id',
        value: sessionId,
        op: 'eq',
      },
    ]);
  }

  if (!enableNW) {
    filter.push([
      {
        field: 'event_name',
        value: 'conviva_network_request',
        op: 'not_eq',
      },
    ]);
  }

  const set = new Set<string>();
  if (globalFilters && globalFilters.length > 0) {
    globalFilters.forEach((andFilter) => {
      andFilter.forEach((orFilter) => {
        set.add(orFilter.field);
      });
    });
  }
  if (enableUserId) {
    const userIdKey = customerOverrideForDrilldown[customer_id] ?? 'app_user_id';
    set.add(userIdKey);
  }
  const extraFields = Array.from(set);

  return http.post<{
    eventList: UserTimelineEvent[];
    requestBody: MarioQuery;
  }>('/appData/timeline/eventList', {
    queries: queries.map((q) => {
      return {
        ...q,
        options: { withTotals: false },
        metrics: [],
        type: '',
        interval,
        granularity: 'ALL',
        filter,
        extraFields,
      };
    }),
  });
};

const getValidGlobalFitlers = (data) => {
  const res = data.map((group) => group.filter((item) => item.value || item.display));
  const allEmpty = res.every((group) => group.length === 0);
  return allEmpty ? [] : res;
};

export const getTimelineImpactedSessions = (
  reqBody,
  options: {
    viewerId: string[];
    ts: string;
    clientId?: string | null;
    globalFilters: Record<string, string>[][];
    eventNames: string[];
  },
  http = instance,
) => {
  const queries = cloneDeep(reqBody.queries);
  const { viewerId, ts, clientId, globalFilters, eventNames = [] } = options;
  const now = new Date();
  const last = new Date(now.getTime() - 24 * 3600000);
  const interval = ts || `${moment(last)}/${moment(now)}`;

  const validGlobalFitlers = getValidGlobalFitlers(globalFilters);

  return http.post<string[]>('/appData/timeline/impactedSessions', {
    queries: queries.map((q) => {
      return {
        ...q,
        options: { withTotals: false },
        metrics: [],
        type: '',
        interval,
        granularity: 'ALL',
        filter: [...validGlobalFitlers, ...getFilter4UserTimeline(viewerId, clientId)],
        eventNames,
      };
    }),
  });
};

export const queryAppData = (body, opts) => instance.post('/metrics', { queries: body }, opts);
